import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CreditCard,
  Shield,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";

import paymentService from "@/services/paymentService";
import authService from "@/services/authService";

const CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
];

const ENCRYPTION_FEES = {
  USD: 25,
  EUR: 23,
  GBP: 20,
  AED: 92,
  INR: 2000,
  CAD: 34,
  AUD: 38,
  SGD: 34,
};

const PURPOSE_LABELS = {
  CROWD_CONTRIBUTION: "Crowdfund Contribution",
  INDIVIDUAL_FUNDING: "Individual Funding",
  ENCRYPTION_ADDON: "Encryption Add-on",
};

export default function PaymentEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Extract params
  const purpose = searchParams.get("purpose") || "CROWD_CONTRIBUTION";
  const entityType = searchParams.get("entity_type") || "BOOK";
  const entityId = searchParams.get("entity_id") || "";
  const initialAmount = parseFloat(searchParams.get("amount")) || 0;
  const initialCurrency = searchParams.get("currency") || "USD";
  const allowEncryption = searchParams.get("allow_encryption") === "true";
  const returnUrl = searchParams.get("return_url");

  // Form state
  const [amount, setAmount] = useState(initialAmount);
  const [currency, setCurrency] = useState(initialCurrency);
  const [encryptionOptIn, setEncryptionOptIn] = useState(false);
  const [payerEmail, setPayerEmail] = useState("");
  const [payerName, setPayerName] = useState("");

  // User state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        setIsAuthenticated(true);
        // Get user email if logged in
        const userData = await authService.getMe().catch(() => null);
        if (userData) {
          setUser(userData);
          setPayerEmail(userData.email);
          setPayerName(userData.name);
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getEncryptionFee = () => {
    if (purpose === "CROWD_CONTRIBUTION") return 0; // Always included
    return ENCRYPTION_FEES[currency] || 25;
  };

  const getTotalAmount = () => {
    const base = amount || 0;
    const encFee = encryptionOptIn ? getEncryptionFee() : 0;
    return base + encFee;
  };

  const getCurrencySymbol = () => {
    return CURRENCIES.find((c) => c.code === currency)?.symbol || "$";
  };

  const handleCreateIntent = async () => {
    if (!entityId) {
      toast.error("Invalid payment request - missing entity ID");
      return;
    }

    if (amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!payerEmail) {
      toast.error("Email is required");
      return;
    }

    try {
      setCreating(true);
      const intent = await paymentService.createIntent({
        purpose,
        entity_type: entityType,
        entity_id: entityId,
        amount,
        currency,
        encryption_opt_in: encryptionOptIn,
        payer_email: payerEmail,
        payer_name: payerName,
        return_url: returnUrl,
      });

      // Navigate to checkout page
      navigate(`/payment/${intent.id}`);
    } catch (error) {
      console.error("Error creating payment intent:", error);
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-entry">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1
                className="text-2xl font-bold text-slate-900 cursor-pointer"
                style={{ fontFamily: "Playfair Display, serif" }}
                onClick={() => navigate("/dashboard")}
              >
                Golden Records
              </h1>
              <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Test Mode
              </Badge>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(returnUrl || "/participate")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h1
              className="text-3xl font-bold text-black"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Secure Checkout
            </h1>
            <p className="text-slate-600 mt-2">
              {PURPOSE_LABELS[purpose] || "Payment"}
            </p>
          </div>

          <Card className="bg-white border-2 border-slate-200 shadow-lg">
            <CardContent className="p-6 space-y-6">
              {/* Amount Section */}
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      {getCurrencySymbol()}
                    </span>
                    <Input
                      id="amount"
                      type="number"
                      value={amount || ""}
                      onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                      className="pl-8"
                      placeholder="0.00"
                      min="1"
                      data-testid="amount-input"
                    />
                  </div>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="w-32" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Encryption Section */}
              {purpose === "CROWD_CONTRIBUTION" ? (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">
                        Encryption Included
                      </p>
                      <p className="text-sm text-green-600">
                        Crowdfunding contributions include encryption at no extra
                        cost
                      </p>
                    </div>
                  </div>
                </div>
              ) : allowEncryption || purpose === "INDIVIDUAL_FUNDING" ? (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-black">Add Encryption</p>
                        <p className="text-sm text-slate-600">
                          +{getCurrencySymbol()}
                          {getEncryptionFee()} for enhanced security
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={encryptionOptIn}
                      onCheckedChange={setEncryptionOptIn}
                      data-testid="encryption-toggle"
                    />
                  </div>
                </div>
              ) : null}

              {/* Payer Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name (optional)</Label>
                  <Input
                    id="name"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="Your name"
                    data-testid="name-input"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-black">
                    {getCurrencySymbol()}
                    {amount.toFixed(2)}
                  </span>
                </div>
                {encryptionOptIn && purpose !== "CROWD_CONTRIBUTION" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Encryption</span>
                    <span className="text-black">
                      {getCurrencySymbol()}
                      {getEncryptionFee().toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span className="text-black">Total</span>
                  <span className="text-purple-600">
                    {getCurrencySymbol()}
                    {getTotalAmount().toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Continue Button */}
              <Button
                onClick={handleCreateIntent}
                disabled={creating || amount <= 0 || !payerEmail}
                className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 py-6 text-lg"
                data-testid="continue-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Continue to Payment
                    <CreditCard className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                You will be redirected to a secure checkout page
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
